<template>
  <div class="widget-body">
    <c-loader v-if="isLoading" />
    <b-table-lite
      v-if="!hasError"
      v-show="!isLoading"
      :fields="fields"
      :items="marketDepthData"
      fixed
      hover
      small
      sticky-header
      striped
      :aria-label="this.$t(`${i18nNamespace}.widget-title`)"
    >
      <template #cell(bid_size)="row">
        <c-volume
          v-if="row.value !== ''"
          class="pr-2"
          :volume="{
            value: row.value,
            pct: (100 / row.item.max_bid_size) * row.value,
            direction: 'rightJustified'
          }"
        />
      </template>

      <template #cell(num_bid)="row">
        <v-fidlet :data="row.value || ''" type="int" />
      </template>

      <template #cell(bid)="row">
        <v-fidlet :data="row.value || ''" type="float.price" />
      </template>

      <template #cell(ask)="row">
        <v-fidlet :data="row.value || ''" type="float.price" />
      </template>

      <template #cell(num_ask)="row">
        <v-fidlet :data="row.value || ''" type="int" />
      </template>

      <template #cell(ask_size)="row">
        <c-volume
          v-if="row.value !== ''"
          class="pl-2"
          :volume="{
            value: row.value,
            pct: (100 / row.item.max_ask_size) * row.value,
            direction: 'leftJustified'
          }"
        />
      </template>
    </b-table-lite>

    <div class="text-center p-2" v-if="hasError">{{ $t(`${i18nNamespace}.error`) }}</div>
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';
import CVolume from '@custom/CVolume';

import { API } from '@/state/api';

const i18nNamespace = 'white-label.widgets.marketDepth';

export default {
  name: 'w-market-depth-widget',
  components: {
    CLoader,
    CVolume
  },
  props: {
    ric: {
      type: String,
      required: true
    },
    view: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      viewOptions: [
        { value: 'by-price', text: this.$t(`${i18nNamespace}.view.options.byPrice`) },
        { value: 'as-ladder', text: this.$t(`${i18nNamespace}.view.options.asLadder`) }
      ],
      fields: [
        {
          key: 'bid_size',
          label: this.$t(`${i18nNamespace}.header.bidSize`),
          class: 'text-right p-0 td-height-fix'
        },
        {
          key: 'num_bid',
          label: this.$t(`${i18nNamespace}.header.numBid`),
          class: 'text-center'
        },
        {
          key: 'bid',
          label: this.$t(`${i18nNamespace}.header.price`),
          class: 'text-right'
        },
        {
          key: 'ask',
          label: this.$t(`${i18nNamespace}.header.price`),
          class: 'text-left'
        },
        {
          key: 'num_ask',
          label: this.$t(`${i18nNamespace}.header.numAsk`),
          class: 'text-center'
        },
        {
          key: 'ask_size',
          label: this.$t(`${i18nNamespace}.header.askSize`),
          class: 'text-left p-0 td-height-fix'
        }
      ],
      rawData: null,
      marketDepthData: null,
      pinnedTopRowData: null
    };
  },
  watch: {
    ric() {
      this.fetch();
    },
    view() {
      this.fetch();
    }
  },
  computed: {
    i18nNamespace() {
      return i18nNamespace;
    }
  },
  methods: {
    onUpdateView(view) {
      this.$store.commit('widget/' + this.wid + '/VIEW_CHANGED', view);
      this.fetch();
    },
    fetch() {
      this.isLoading = true;
      this.hasError = false;

      this.getMarketDepthData()
        .then(response => {
          if (response.data.data[0].bid === 'n.a.') {
            throw new Error('NO_MARKET_DEPTH_DATA_RECEIVED');
          }

          this.rawData = this.processData(response.data.data);

          this.marketDepthData = this.getViewData(this.view);

          this.isLoading = false;
        })
        .catch(error => {
          this.hasError = true;
          this.isLoading = false;
          console.log(error);
        });
    },
    getMarketDepthData() {
      return API.Rest.get(`/marketdepth/orderbook/?ric=${this.ric}`);
    },
    getViewData(view) {
      if (view === 'by-price') {
        return this.pricify(this.rawData);
      }
      return this.ladderize(this.rawData);
    },
    processData(data) {
      const _maxAskSize = Math.max.apply(Math, data.map(o => o.ask_size));
      const _maxBidSize = Math.max.apply(Math, data.map(o => o.bid_size));

      /*
       * No metadata available via current REST endpoint and that will only change with a new endpoint (which is hopefully available soon)
       */
      return data.map(o => ({
        ...o,
        ask: o.ask,
        bid: o.bid,
        ask_size: o.ask_size,
        bid_size: o.bid_size,
        num_ask: o.num_ask,
        num_bid: o.num_bid,
        max_ask_size: _maxAskSize,
        max_bid_size: _maxBidSize
      }));
    },
    pricify(marketDepthData) {
      return marketDepthData.map((o, i) => ({
        ...o,
        _rowVariant: this.getRowVariant('by-price', i)
      }));
    },
    getRowVariant(view, index) {
      return `${view}-${index}`;
    },
    ladderize(marketDepthData) {
      const data = marketDepthData.concat(marketDepthData);

      const result = [];

      data.forEach((element, i) => {
        let tmp = {};

        if (i < 10) {
          tmp.ask = element.ask;
          tmp.ask_size = element.ask_size;
          tmp.num_ask = element.num_ask;
          tmp.bid = '';
          tmp.bid_size = '';
          tmp.num_bid = '';
          tmp.max_ask_size = element.max_ask_size;
          tmp._rowVariant = this.getRowVariant('as-ladder', i);
          result.unshift(tmp);
        } else {
          tmp.ask = '';
          tmp.ask_size = '';
          tmp.num_ask = '';
          tmp.bid = element.bid;
          tmp.bid_size = element.bid_size;
          tmp.num_bid = element.num_bid;
          tmp.max_bid_size = element.max_bid_size;
          tmp._rowVariant = this.getRowVariant('as-ladder', i);
          result.push(tmp);
        }
      });

      result.unshift({
        ...marketDepthData[0],
        _rowVariant: 'as-ladder-top-row'
      });

      return result;
    }
  },
  mounted() {
    this.fetch();
  }
};
</script>
